import { toastError } from "../../utils/toast";

function normalizeText(text: string): string {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value
    .normalize("NFC")
    .replace(/[\u2018\u2019\u201A\u201B\u2039\u203A]/gu, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u00AB\u00BB]/gu, '"')
    .replace(/[\u2010-\u2015\u2212]/gu, "-")
    .replace(/\u2026/gu, "...")
    .replace(/(?![\u200C\u200D])\p{C}/gu, "")
    .replace(/\s+/gu, "")
    .toLowerCase();
}

function textFromRunTags(xmlString: string): string {
  const textMatches = xmlString.match(/<w:t[^>]*>([^<]*)<\/w:t>/g);
  return textMatches
    ? normalizeText(textMatches.map((m) => m.replace(/<w:t[^>]*>([^<]*)<\/w:t>/, "$1")).join(""))
    : "";
}

function getTextFromXml(xmlString: string, onError: (e: unknown) => void): string {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");

    if (xmlDoc.querySelector("parsererror")) {
      return textFromRunTags(xmlString);
    }

    const getTextNodes = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
      let text = "";
      for (let i = 0; i < node.childNodes.length; i++) {
        text += getTextNodes(node.childNodes[i]);
      }
      return text;
    };

    return normalizeText(getTextNodes(xmlDoc));
  } catch (error) {
    onError(error);
    return textFromRunTags(xmlString);
  }
}

function extractXmlData(part: Element | null): string | null {
  if (!part) return null;
  const xmlData = part.getElementsByTagName("pkg:xmlData")[0] || part.getElementsByTagName("xmlData")[0];
  if (xmlData && xmlData.firstElementChild) {
    return new XMLSerializer().serializeToString(xmlData.firstElementChild);
  }
  return null;
}

function extractOoxmlParts(ooxmlString: string, onError: (e: unknown) => void): { comments: string | null; document: string | null } {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(ooxmlString, "text/xml");

    if (xmlDoc.querySelector("parsererror")) {
      onError(new Error("Failed to parse OOXML as XML"));
      return { comments: null, document: null };
    }

    const allParts = xmlDoc.getElementsByTagName("pkg:part");
    let commentsPart: Element | null = null;
    let documentPart: Element | null = null;

    for (let i = 0; i < allParts.length; i++) {
      const part = allParts[i];
      const name = part.getAttribute("pkg:name") || part.getAttribute("name");
      if (name === "/word/comments.xml") commentsPart = part;
      else if (name === "/word/document.xml") documentPart = part;
    }

    return { comments: extractXmlData(commentsPart), document: extractXmlData(documentPart) };
  } catch (error) {
    toastError(error);
    onError(error);
    return { comments: null, document: null };
  }
}

export function compareOoxmlContent(ooxml1: string, ooxml2: string, onError: (e: unknown) => void): { matches: boolean; textDiff: boolean; commentsDiff: boolean } {
  const parts1 = extractOoxmlParts(ooxml1, onError);
  const parts2 = extractOoxmlParts(ooxml2, onError);
  const commentsText1 = parts1.comments ? getTextFromXml(parts1.comments, onError) : "";
  const commentsText2 = parts2.comments ? getTextFromXml(parts2.comments, onError) : "";
  const commentsMatch = commentsText1 === commentsText2;
  const documentText1 = parts1.document === null ? null : getTextFromXml(parts1.document, onError);
  const documentText2 = parts2.document === null ? null : getTextFromXml(parts2.document, onError);
  const documentTextMatch = documentText1 !== null && documentText1 === documentText2;
  return {
    matches: commentsMatch && documentTextMatch,
    textDiff: !documentTextMatch,
    commentsDiff: !commentsMatch,
  };
}
